import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { WalletCard, WalletStats, ShareWalletButton } from "@/components/WalletCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { Gift, ShieldCheck, RefreshCw, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

export default function Carteira() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [openQr, setOpenQr] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef(null);

  const fetchWallet = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.get("/wallet", { timeout: 15000 });
      const payload = res?.data;
      if (!payload || !payload.user) {
        throw new Error("Resposta da carteira está incompleta");
      }
      setData(payload);
      if (setUser) setUser(payload.user);
      setSecondsLeft(payload.qr_ttl_seconds || 60);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Não foi possível carregar a carteira agora.";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto refresh QR while dialog is open
  useEffect(() => {
    if (refreshTimer.current) { clearInterval(refreshTimer.current); refreshTimer.current = null; }
    if (!openQr || !data) return;
    setSecondsLeft(data?.qr_ttl_seconds || 60);
    refreshTimer.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          fetchWallet();
          return data?.qr_ttl_seconds || 60;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openQr]);

  // Estado: erro de carregamento — fallback amigável com botão tentar novamente
  if (error && !data) {
    return (
      <div className="space-y-3" data-testid="carteira-error">
        <Card className="rounded-2xl border-amber-200 bg-amber-50/50">
          <CardContent className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-display font-semibold text-[hsl(var(--primary))]">
              Não foi possível carregar a carteira
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">
              Verifique sua conexão com a internet e tente novamente. Seus dados estão seguros.
            </p>
            <Button onClick={fetchWallet} className="mt-4" data-testid="carteira-retry">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Estado: carregando
  if (loading || !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-[220px] w-full rounded-2xl" />
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-[68px] rounded-xl" />
          <Skeleton className="h-[68px] rounded-xl" />
          <Skeleton className="h-[68px] rounded-xl" />
        </div>
      </div>
    );
  }

  const walletUser = data.user || user || {};

  return (
    <div className="space-y-4" data-testid="carteira-page">
      <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Carteira Digital</h1>
      <WalletCard
        user={walletUser}
        qrImage={data.qr_image}
        onShowQr={() => setOpenQr(true)}
        seal={data.seal}
        progress={data.progress}
      />
      <WalletStats user={walletUser} />
      <div className="grid grid-cols-2 gap-3">
        <Button onClick={() => setOpenQr(true)} className="h-11" data-testid="wallet-show-qr">Apresentar carteira</Button>
        <ShareWalletButton user={walletUser} />
      </div>
      <Card className="rounded-xl">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-display font-medium text-sm text-[hsl(var(--primary))]">
                Nível: {walletUser.level || "Aluno CEAP"}
              </p>
              <p className="text-xs text-muted-foreground">Conclua mais cursos para evoluir seu nível.</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Link to="/beneficios">
        <Card className="rounded-xl hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--secondary))] text-[hsl(var(--primary))] flex items-center justify-center">
              <Gift className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-medium text-sm text-[hsl(var(--primary))]">Ver convênios do aluno</p>
              <p className="text-xs text-muted-foreground">Convênios e descontos exclusivos CEAP</p>
            </div>
          </CardContent>
        </Card>
      </Link>

      <Dialog open={openQr} onOpenChange={setOpenQr}>
        <DialogContent className="max-w-[420px] rounded-2xl" data-testid="wallet-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Sua carteira CEAP</DialogTitle>
            <DialogDescription>Apresente este QR Code ao operador na entrada e saída do curso.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {data.qr_image ? (
              <div className="rounded-2xl border border-[hsl(var(--gold)/0.45)] bg-white p-4 shadow-sm relative">
                <img src={data.qr_image} alt="QR" className="w-[260px] h-[260px]" />
                <div className="absolute -top-2 -right-2 px-2.5 py-1 rounded-full bg-[hsl(var(--accent))] text-white text-[10.5px] font-medium flex items-center gap-1 shadow-md">
                  <RefreshCw className="w-3 h-3" /> Renova em {secondsLeft}s
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-8 flex items-center justify-center w-[260px] h-[260px]">
                <p className="text-xs text-muted-foreground text-center">QR indisponível. Toque em "Tentar novamente".</p>
              </div>
            )}
            <p className="font-display font-semibold text-[hsl(var(--primary))]">{walletUser.full_name || "Aluno CEAP"}</p>
            <p className="font-mono-id text-xs text-muted-foreground">{walletUser.ceap_id || "—"}</p>
            <p className="text-[11px] text-muted-foreground text-center max-w-xs">QR válido por curtos períodos. Para sua segurança, é renovado automaticamente.</p>
            <Button variant="outline" size="sm" onClick={fetchWallet} className="mt-1" data-testid="wallet-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Atualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
